import { useState } from 'react';
import { crmContacts, crmDealArchive, crmDealContactLink, crmDealRestore, type CrmContact } from '@aithora/shared';
import { useSession } from '../../../session/SessionContext';
import { sx } from '../dc';

// Two deal controls restored from the production CRM (crm_view.dart): link a person to the
// deal, and archive the deal (kept, hidden; Restore brings it back at once). Not in the V3
// design: one quiet line under the deal's actions.

const LINK = 'font-size:14px;font-weight:600;color:#2F5C97;text-decoration:none;white-space:nowrap;';
const INPUT = 'box-sizing:border-box;height:28px;padding:0 8px;border:1px solid rgba(34,39,46,.18);border-radius:6px;background:#FBF8F1;font-size:16px;color:#22272E;font-family:inherit;flex:1 1 140px;min-width:0;';

export function DealExtras({ dealId, writable, writeTitle }: { dealId: string; writable: boolean; writeTitle?: string }) {
  const { client } = useSession();
  const [mode, setMode] = useState<'idle' | 'link'>('idle');
  const [q, setQ] = useState('');
  const [found, setFound] = useState<CrmContact[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [archived, setArchived] = useState(false);
  const say = (e: unknown) => setMsg(e instanceof Error ? e.message : 'That did not go through.');
  const search = async () => {
    if (!q.trim()) return;
    try { setFound((await crmContacts(client, { query: { q: q.trim(), limit: 6 } })).contacts ?? []); } catch (e) { say(e); }
  };
  const link = async (c: CrmContact) => {
    try {
      await crmDealContactLink(client, { params: { id: dealId }, body: { contactId: c.id } });
      setMsg(`${c.name ?? 'The contact'} is linked to this deal.`);
      setMode('idle'); setFound([]); setQ('');
    } catch (e) { say(e); }
  };
  const archive = async () => {
    try { await crmDealArchive(client, { params: { id: dealId }, body: {} }); setArchived(true); setMsg('Archived: kept, hidden from the lists.'); } catch (e) { say(e); }
  };
  const restore = async () => {
    try { await crmDealRestore(client, { params: { id: dealId }, body: {} }); setArchived(false); setMsg('Restored.'); } catch (e) { say(e); }
  };
  const off = !writable;
  return (
    <div data-testid="crm-deal-extras" style={sx('display:flex;flex-direction:column;gap:5px;')}>
      <div style={sx('display:flex;gap:12px;flex-wrap:wrap;')}>
        <a href="#" aria-disabled={off || undefined} title={off ? writeTitle : undefined} onClick={(e) => { e.preventDefault(); if (!off) setMode(mode === 'link' ? 'idle' : 'link'); }} style={sx(LINK + (off ? 'color:#54606C;pointer-events:none;' : ''))}>
          Link a contact
        </a>
        {archived ? (
          <a href="#" onClick={(e) => { e.preventDefault(); void restore(); }} style={sx(LINK)}>Restore deal</a>
        ) : (
          <a href="#" aria-disabled={off || undefined} title={off ? writeTitle : undefined} onClick={(e) => { e.preventDefault(); if (!off) void archive(); }} style={sx(LINK + 'color:#54606C;' + (off ? 'pointer-events:none;' : ''))}>
            Archive deal
          </a>
        )}
      </div>
      {mode === 'link' && (
        <div style={sx('display:flex;flex-direction:column;gap:4px;')}>
          <div style={sx('display:flex;gap:6px;')}>
            <input aria-label="Find a contact" placeholder="Name or email" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} style={sx(INPUT)} />
            <a href="#" onClick={(e) => { e.preventDefault(); void search(); }} style={sx(LINK)}>Find</a>
          </div>
          {found.map((c) => (
            <a key={c.id} href="#" onClick={(e) => { e.preventDefault(); void link(c); }} style={sx('font-size:16px;color:#22272E;text-decoration:none;padding:3px 0;')}>
              {[c.name, c.title, c.email].filter(Boolean).join(' · ')}
            </a>
          ))}
        </div>
      )}
      {msg && <span role="status" style={sx('font-size:14px;color:#3C454F;')}>{msg}</span>}
    </div>
  );
}
